import {
  Home,
  Layers,
  LogOut,
  Search,
  LinkIcon,
  ShoppingBag,
  ClipboardList,
  Building2,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Link, useLocation } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarMenuSub,
  SidebarMenuSubItem,
  SidebarMenuSubButton,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from "@/components/ui/sidebar";
import { useAuth } from "@/context/AuthContext";
import { useOrganization } from "@/context/OrganizationContext";
import { useTasks } from "@/context/TasksContext";
import { Sheet, SheetContent } from "@/components/ui/sheet";

export function DashboardSidebar() {
  const location = useLocation();
  const pathname = location.pathname;
  const { openMobile, setOpenMobile } = useSidebar();
  const { signOut } = useAuth();
  const { can } = useOrganization();
  const { getPendingCount } = useTasks();

  // Navigation items shared between mobile and desktop
  const navigationItems = [
    { path: "/", label: "Dashboard", icon: <Home className="h-5 w-5" /> },
  ];
  const isCategoriesAttributesActive = pathname === "/catalog/categories-attributes";
  const isModerationRulesActive = pathname === "/catalog/moderation-rules";
  const isCataloguesActive =
    pathname === "/catalog" ||
    (pathname.startsWith("/catalog/") &&
      !isCategoriesAttributesActive &&
      !isModerationRulesActive);
  const isProductCatalogActive = pathname === "/catalog" || pathname.startsWith("/catalog/");
  const productCatalogItems = [
    { path: "/catalog", label: "Catalogues", isActive: isCataloguesActive },
    {
      path: "/catalog/categories-attributes",
      label: "Categories & Attributes",
      isActive: isCategoriesAttributesActive,
    },
    {
      path: "/catalog/moderation-rules",
      label: "Moderation Rules",
      isActive: isModerationRulesActive,
    },
  ];

  // Logo component used in both mobile and desktop
  const Logo = () => (
    <div className="flex items-center gap-2">
      <img src="/logo.svg" alt="Youzu.ai" className="h-8 w-8" />
      <span className="text-xl font-bold bg-gradient-to-r from-pink-500 to-purple-500 bg-clip-text text-transparent">
        Youzu.ai
      </span>
      <Badge
        variant="secondary"
        className="ml-1 text-[10px] px-1.5 py-0 h-5 bg-fuchsia-700 text-white hover:bg-fuchsia-700 border-fuchsia-700"
      >
        Beta
      </Badge>
    </div>
  );

  return (
    <>
      {/* Desktop Sidebar */}
      <Sidebar className="hidden md:block">
        <SidebarHeader className="flex flex-col gap-4 px-4 py-4">
          <Logo />
        </SidebarHeader>
        <SidebarSeparator />
        <SidebarContent>
          <SidebarMenu>
            {navigationItems.map((item) => (
              <SidebarMenuItem key={item.path}>
                <SidebarMenuButton asChild isActive={pathname === item.path}>
                  <Link to={item.path}>
                    {item.icon}
                    <span>{item.label}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            ))}
            {can("catalog") && (
            <SidebarMenuItem key="product-catalog">
              <SidebarMenuButton isActive={isProductCatalogActive}>
                <ShoppingBag className="h-5 w-5" />
                <span>Product Catalog</span>
              </SidebarMenuButton>
              <SidebarMenuSub>
                {productCatalogItems.map((item) => (
                  <SidebarMenuSubItem key={item.path}>
                    <SidebarMenuSubButton asChild isActive={item.isActive}>
                      <Link to={item.path}>
                        <span>{item.label}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </SidebarMenuItem>
            )}
            {can("ai_tools") && (
            <>
            <SidebarMenuItem key="discovery">
              <SidebarMenuButton isActive={pathname === "/youzu-lens"}>
                <Search className="h-5 w-5" />
                <span>Discovery</span>
              </SidebarMenuButton>
              <SidebarMenuSub>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={pathname === "/youzu-lens"}
                  >
                    <Link to="/youzu-lens">
                      <span>Youzu Lens</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            </SidebarMenuItem>
            <SidebarMenuItem key="room-intelligence">
              <SidebarMenuButton
                isActive={[
                  "/room-visualizer",
                  "/youzu-swap",
                ].some((p) => pathname.startsWith(p))}
              >
                <Layers className="h-5 w-5" />
                  <span>Space Intelligence</span>
              </SidebarMenuButton>
              <SidebarMenuSub>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={pathname === "/room-visualizer"}
                  >
                     <Link to="/room-visualizer">
                       <span>Room Visualizer</span>
                     </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={pathname === "/youzu-swap"}
                  >
                    <Link to="/youzu-swap">
                      <span>Youzu Swap</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            </SidebarMenuItem>{" "}
            <SidebarMenuItem key="interactive">
              <SidebarMenuButton isActive={["/magic-background", "/2d-to-3d"].some((p) => pathname.startsWith(p))}>
                <Layers className="h-5 w-5" />
                <span>Interactive</span>
              </SidebarMenuButton>
              <SidebarMenuSub>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={pathname === "/magic-background"}
                  >
                    <Link to="/magic-background">
                      <span>Magic Background</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
                <SidebarMenuSubItem>
                  <SidebarMenuSubButton
                    asChild
                    isActive={pathname === "/2d-to-3d"}
                  >
                    <Link to="/2d-to-3d">
                      <span>2D to 3D</span>
                    </Link>
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              </SidebarMenuSub>
            </SidebarMenuItem>
            </>
            )}
            {can("api_keys") && (
            <SidebarMenuItem key="integration-hub">
              <SidebarMenuButton
                asChild
                isActive={pathname === "/integration-hub"}
              >
                <Link to="/integration-hub">
                  <LinkIcon className="h-5 w-5" />
                  <span>Integration Hub</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            )}
            <SidebarMenuItem key="organization">
              <SidebarMenuButton asChild isActive={pathname === "/organization"}>
                <Link to="/organization">
                  <Building2 className="h-5 w-5" />
                  <span>Organization</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarContent>
        <SidebarSeparator />
        <SidebarFooter>
          <SidebarMenu>
            {can("ai_tools") && (
            <SidebarMenuItem>
              <SidebarMenuButton asChild isActive={pathname === "/tasks"}>
                <Link to="/tasks">
                  <ClipboardList className="h-5 w-5" />
                  <span>Tasks</span>
                  {getPendingCount() > 0 && (
                    <Badge
                      variant="secondary"
                      className="ml-auto bg-pink-500 text-white text-xs px-2 py-0 h-5 min-w-[20px] flex items-center justify-center"
                    >
                      {getPendingCount()}
                    </Badge>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
            )}
            <SidebarMenuItem>
              <SidebarMenuButton asChild onClick={() => signOut()}>
                <button>
                  <LogOut className="h-5 w-5" />
                  <span>Logout</span>
                </button>
              </SidebarMenuButton>
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarFooter>
        <SidebarRail />
      </Sidebar>

      {/* Mobile Sidebar */}
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent side="left" className="p-0 w-[280px]">
          <div className="flex items-center p-4 border-b">
            <Logo />
          </div>
          <div className="py-4">
            <SidebarMenu>
              {navigationItems.map((item) => (
                <SidebarMenuItem key={item.path}>
                  <SidebarMenuButton
                    asChild
                    isActive={pathname === item.path}
                    onClick={() => setOpenMobile(false)}
                  >
                    <Link to={item.path}>
                      {item.icon}
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {can("catalog") && (
              <SidebarMenuItem key="product-catalog">
                <SidebarMenuButton isActive={isProductCatalogActive}>
                  <ShoppingBag className="h-5 w-5" />
                  <span>Product Catalog</span>
                </SidebarMenuButton>
                <SidebarMenuSub>
                  {productCatalogItems.map((item) => (
                    <SidebarMenuSubItem key={item.path}>
                      <SidebarMenuSubButton
                        asChild
                        isActive={item.isActive}
                        onClick={() => setOpenMobile(false)}
                      >
                        <Link to={item.path}>
                          <span>{item.label}</span>
                        </Link>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </SidebarMenuItem>
              )}

              {can("ai_tools") && (
              <>
              <SidebarMenuItem key="discovery">
                <SidebarMenuButton isActive={pathname === "/youzu-lens"}>
                  <Search className="h-5 w-5" />
                  <span>Discovery</span>
                </SidebarMenuButton>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={pathname === "/youzu-lens"}
                      onClick={() => setOpenMobile(false)}
                    >
                      <Link to="/youzu-lens">
                        <span>Youzu Lens</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </SidebarMenuItem>

              <SidebarMenuItem key="room-intelligence">
                <SidebarMenuButton
                  isActive={[
                    "/room-visualizer",
                    "/youzu-swap",
                  ].some((p) => pathname.startsWith(p))}
                >
                  <Layers className="h-5 w-5" />
                <span>Space Intelligence</span>
                </SidebarMenuButton>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={pathname === "/room-visualizer"}
                      onClick={() => setOpenMobile(false)}
                    >
                     <Link to="/room-visualizer">
                         <span>Room Visualizer</span>
                       </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={pathname === "/youzu-swap"}
                      onClick={() => setOpenMobile(false)}
                    >
                      <Link to="/youzu-swap">
                        <span>Youzu Swap</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </SidebarMenuItem>

              <SidebarMenuItem key="interactive">
                <SidebarMenuButton isActive={["/magic-background", "/2d-to-3d"].some((p) => pathname.startsWith(p))}>
                  <Layers className="h-5 w-5" />
                  <span>Interactive</span>
                </SidebarMenuButton>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={pathname === "/magic-background"}
                      onClick={() => setOpenMobile(false)}
                    >
                      <Link to="/magic-background">
                        <span>Magic Background</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton
                      asChild
                      isActive={pathname === "/2d-to-3d"}
                      onClick={() => setOpenMobile(false)}
                    >
                      <Link to="/2d-to-3d">
                        <span>2D to 3D</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </SidebarMenuItem>
              </>
              )}

              {can("api_keys") && (
              <SidebarMenuItem key="integration-hub">
                <SidebarMenuButton
                  asChild
                  isActive={pathname === "/integration-hub"}
                  onClick={() => setOpenMobile(false)}
                >
                  <Link to="/integration-hub">
                    <LinkIcon className="h-5 w-5" />
                    <span>Integration Hub</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              )}

              <SidebarMenuItem key="organization">
                <SidebarMenuButton
                  asChild
                  isActive={pathname === "/organization"}
                  onClick={() => setOpenMobile(false)}
                >
                  <Link to="/organization">
                    <Building2 className="h-5 w-5" />
                    <span>Organization</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
            <SidebarSeparator className="my-4" />
            <SidebarMenu>
              {can("ai_tools") && (
              <SidebarMenuItem>
                <SidebarMenuButton
                  asChild
                  isActive={pathname === "/tasks"}
                  onClick={() => setOpenMobile(false)}
                >
                  <Link to="/tasks">
                    <ClipboardList className="h-5 w-5" />
                    <span>Tasks</span>
                    {getPendingCount() > 0 && (
                      <Badge
                        variant="secondary"
                        className="ml-auto bg-pink-500 text-white text-xs px-2 py-0 h-5 min-w-[20px] flex items-center justify-center"
                      >
                        {getPendingCount()}
                      </Badge>
                    )}
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild onClick={() => signOut()}>
                  <button>
                    <LogOut className="h-5 w-5" />
                    <span>Logout</span>
                  </button>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
